module Client.Components.Graph.FourTimesSoilSensor

open Client.Components.Graph.Recharts
open Client.Components.GraphCommon
open Client.Domain.MeasuredValueString
open Feliz.Recharts
open Fulma
open Shared.Dto.MapSensorData
open Shared.Dto.SensorGraphData
open Shared.Infrastructure

let private yAxisWidth = yAxis.width 90
let private xAxisTickMargin = xAxis.tickMargin 8

type FourTimesSoilGraphNode = {
    Ticks: double

    Temperature1: float
    SoilMoisture1: float
    SoilConductivity1: float

    Temperature2: float option
    SoilMoisture2: float option
    SoilConductivity2: float option

    Temperature3: float option
    SoilMoisture3: float option
    SoilConductivity3: float option

    Temperature4: float option
    SoilMoisture4: float option
    SoilConductivity4: float option
}

type FourTimesSoilSensorGraphData = {
    TemperatureAxisTicks: float list
    MoistureAxisTicks: float list
    ConductivityAxisTicks: float list
    Data: FourTimesSoilGraphNode list
}

let private temperatureAxisTicks (data: FourTimesSoilGraphNode list) =
    let minValue =
        data
        |> List.map (fun node ->
            [
                Some node.Temperature1
                node.Temperature2
                node.Temperature3
                node.Temperature4
            ]
            |> List.choose id
            |> List.min
        )
        |> List.min

    let maxValue =
        data
        |> List.map (fun node ->
            [
                Some node.Temperature1
                node.Temperature2
                node.Temperature3
                node.Temperature4
            ]
            |> List.choose id
            |> List.max
        )
        |> List.max

    AxisTicks.generateBy true 5. minValue maxValue

let private humidityAxisTicks (data: FourTimesSoilGraphNode list) =
    let minValue =
        data
        |> List.map (fun node ->
            [
                Some node.SoilMoisture1
                node.SoilMoisture2
                node.SoilMoisture3
                node.SoilMoisture4
            ]
            |> List.choose id
            |> List.min
        )
        |> List.min

    let maxValue =
        data
        |> List.map (fun node ->
            [
                Some node.SoilMoisture1
                node.SoilMoisture2
                node.SoilMoisture3
                node.SoilMoisture4
            ]
            |> List.choose id
            |> List.max
        )
        |> List.max

    AxisTicks.generateBy true 5. minValue maxValue

let private conductivityAxisTicks (data: FourTimesSoilGraphNode list) =
    let minValue =
        data
        |> List.map (fun node ->
            [
                Some node.SoilConductivity1
                node.SoilConductivity2
                node.SoilConductivity3
                node.SoilConductivity4
            ]
            |> List.choose id
            |> List.min
        )
        |> List.min

    let maxValue =
        data
        |> List.map (fun node ->
            [
                Some node.SoilConductivity1
                node.SoilConductivity2
                node.SoilConductivity3
                node.SoilConductivity4
            ]
            |> List.choose id
            |> List.max
        )
        |> List.max

    AxisTicks.generateBy true 100. minValue maxValue

let createFourTimesSoilSensorGraphData (data: FourTimesSoilGraphNode list) =
    let temperatureAxis = temperatureAxisTicks data

    let humidityAxis = humidityAxisTicks data

    let conductivityAxis = conductivityAxisTicks data

    {
        TemperatureAxisTicks = temperatureAxis
        MoistureAxisTicks = humidityAxis
        ConductivityAxisTicks = conductivityAxis
        Data = data
    }

let toSoilSensorGraphData (data: GraphFourTimesSoilDataDto) = {
    Ticks = data.TimeStamp.ToUnixTimeSeconds() |> double

    Temperature1 = data.SoilTemperature1
    SoilMoisture1 = data.SoilMoisture1
    SoilConductivity1 = data.SoilConductivity1

    Temperature2 = data.SoilTemperature2
    SoilMoisture2 = data.SoilMoisture2
    SoilConductivity2 = data.SoilConductivity2

    Temperature3 = data.SoilTemperature3
    SoilMoisture3 = data.SoilMoisture3
    SoilConductivity3 = data.SoilConductivity3

    Temperature4 = data.SoilTemperature4
    SoilMoisture4 = data.SoilMoisture4
    SoilConductivity4 = data.SoilConductivity4
}

let mapDataToSoil (data: GraphDataDto) =
    match data with
    | FourTimesSoil soilData -> Some soilData
    | _ -> None

let graphDataDtoToFourTimesSoilGraphData (data: GraphFourTimesSoilDataDto list) : FourTimesSoilSensorGraphData =
    let list = data |> List.map toSoilSensorGraphData

    createFourTimesSoilSensorGraphData list

let currentSoilDataBox (data: FourTimesSoilSensorData) =
    let mapOptionalFloat = fun f x -> x |> Option.map f |> Option.defaultValue "-"

    [
        Level.level [] [
            currentDataLevelItem "Wann?" (DateTime.toDateTimeString data.Date.LocalDateTime)
        ]

        Level.level [] [
            currentDataLevelItem "Aktuelle Bodenfeuchtigkeit #1" (percentageToString data.SoilMoisture1)
            currentDataLevelItem "Aktuelle Temperatur #1" (temperatureToString data.SoilTemperature1)
            currentDataLevelItem "Aktuelle Leitfähigkeit #1" (data.SoilConductivity1 |> conductivityToString)
        ]

        Level.level [] [
            currentDataLevelItem
                "Aktuelle Bodenfeuchtigkeit #2"
                (data.SoilMoisture2 |> mapOptionalFloat percentageToString)
            currentDataLevelItem
                "Aktuelle Temperatur #2"
                (data.SoilTemperature2 |> mapOptionalFloat temperatureToString)
            currentDataLevelItem
                "Aktuelle Leitfähigkeit #2"
                (data.SoilConductivity2 |> mapOptionalFloat conductivityToString)
        ]

        Level.level [] [
            currentDataLevelItem
                "Aktuelle Bodenfeuchtigkeit #3"
                (data.SoilMoisture3 |> mapOptionalFloat percentageToString)
            currentDataLevelItem
                "Aktuelle Temperatur #3"
                (data.SoilTemperature3 |> mapOptionalFloat temperatureToString)
            currentDataLevelItem
                "Aktuelle Leitfähigkeit #3"
                (data.SoilConductivity3 |> mapOptionalFloat conductivityToString)
        ]

        Level.level [] [
            currentDataLevelItem
                "Aktuelle Bodenfeuchtigkeit #4"
                (data.SoilMoisture4 |> mapOptionalFloat percentageToString)
            currentDataLevelItem
                "Aktuelle Temperatur #4"
                (data.SoilTemperature4 |> mapOptionalFloat temperatureToString)
            currentDataLevelItem
                "Aktuelle Leitfähigkeit #4"
                (data.SoilConductivity4 |> mapOptionalFloat conductivityToString)
        ]
    ]

let fourTimesSoilDataGraphs (graphData: FourTimesSoilSensorGraphData) =
    let xDomain = xAxis.domain (domain.min, domain.max)

    let yDomain = yAxis.domain (domain.min, domain.max)

    let xAxisTickInterval = xAxis.interval.preserveStartEnd

    let dataKey = (fun (p: FourTimesSoilGraphNode) -> p.Ticks) |> xAxis.dataKey

    let humidityAxisTicks = AxisTicks.createYProperty graphData.MoistureAxisTicks

    let humidityChart =
        Recharts.lineChart [
            lineChart.syncId "syncId1"
            lineChart.data graphData.Data
            lineChart.children [
                Recharts.xAxis [
                    dataKey
                    xAxis.number
                    xAxis.scale.time
                    xAxis.tickFormatter formatTimeTick
                    xDomain
                    xAxisTickInterval
                    xAxisTickMargin
                ]
                Recharts.yAxis [
                    yAxisWidth
                    yAxis.unit "%"
                    yAxis.number
                    humidityAxisTicks
                    yAxis.tickCount 6
                    yDomain
                ]
                Recharts.legend []
                Recharts.tooltip [ tooltip.content tooltipContent ]

                Recharts.line [
                    line.dot false
                    line.dataKey (fun p -> p.SoilMoisture1)
                    line.name "Bodenfeuchtigkeit 1 [%]"
                    line.stroke "#66A61E"
                ]

                Recharts.line [
                    line.dot false
                    line.dataKey (fun p -> p.SoilMoisture2)
                    line.name "Bodenfeuchtigkeit 2 [%]"
                    line.stroke "#E6AB02"
                ]

                Recharts.line [
                    line.dot false
                    line.dataKey (fun p -> p.SoilMoisture3)
                    line.name "Bodenfeuchtigkeit 3 [%]"
                    line.stroke "#1B9E77"
                ]

                Recharts.line [
                    line.dot false
                    line.dataKey (fun p -> p.SoilMoisture4)
                    line.name "Bodenfeuchtigkeit 4 [%]"
                    line.stroke "#D95F02"
                ]

                Recharts.cartesianGrid [ cartesianGrid.strokeDasharray [| 3; 3 |] ]
            ]
        ]
        |> fullHeightGraphBox "Bodenfeuchtigkeit"

    let temperatureAxisTicks = AxisTicks.createYProperty graphData.TemperatureAxisTicks

    let temperatureChart =
        Recharts.lineChart [
            lineChart.syncId "syncId1"
            lineChart.data graphData.Data
            lineChart.children [
                Recharts.xAxis [
                    dataKey
                    xAxis.number
                    xAxis.scale.time
                    xAxis.tickFormatter formatTimeTick
                    xDomain
                    xAxisTickInterval
                    xAxisTickMargin
                ]
                Recharts.yAxis [
                    yAxisWidth
                    yAxis.unit " °C"
                    temperatureAxisTicks
                    yDomain
                ]
                Recharts.legend []
                Recharts.tooltip [ tooltip.content tooltipContent ]
                Recharts.line [
                    line.dot false
                    line.dataKey (fun p -> p.Temperature1)
                    line.name "Bodentemperatur 1 [°C]"
                    line.stroke "#66A61E"
                ]

                Recharts.line [
                    line.dot false
                    line.dataKey (fun p -> p.Temperature2)
                    line.name "Bodentemperatur 2 [°C]"
                    line.stroke "#E6AB02"
                ]

                Recharts.line [
                    line.dot false
                    line.dataKey (fun p -> p.Temperature3)
                    line.name "Bodentemperatur 3 [°C]"
                    line.stroke "#1B9E77"
                ]

                Recharts.line [
                    line.dot false
                    line.dataKey (fun p -> p.Temperature4)
                    line.name "Bodentemperatur 4 [°C]"
                    line.stroke "#D95F02"
                ]

                Recharts.cartesianGrid [ cartesianGrid.strokeDasharray [| 3; 3 |] ]
            ]
        ]
        |> fullHeightGraphBox "Verlauf der Bodentemperatur"

    let conductivityAxisTicks =
        AxisTicks.createYProperty graphData.ConductivityAxisTicks

    let conductivityChart =
        Recharts.lineChart [
            lineChart.syncId "syncId1"
            lineChart.data graphData.Data
            lineChart.children [
                Recharts.xAxis [
                    dataKey
                    xAxis.number
                    xAxis.scale.time
                    xAxis.tickFormatter formatTimeTick
                    xDomain
                    xAxisTickInterval
                    xAxisTickMargin
                ]
                Recharts.yAxis [
                    yAxisWidth
                    yAxis.unit " μS/cm"
                    conductivityAxisTicks
                    yDomain
                ]
                Recharts.legend []
                Recharts.tooltip [ tooltip.content tooltipContent ]
                Recharts.line [
                    line.dot false
                    line.dataKey (fun p -> p.SoilConductivity1)
                    line.name "Leitfähigkeit 1 [μS/cm]"
                    line.stroke "#66A61E"
                ]

                Recharts.line [
                    line.dot false
                    line.dataKey (fun p -> p.SoilConductivity2)
                    line.name "Leitfähigkeit 2 [μS/cm]"
                    line.stroke "#E6AB02"
                ]

                Recharts.line [
                    line.dot false
                    line.dataKey (fun p -> p.SoilConductivity3)
                    line.name "Leitfähigkeit 3 [μS/cm]"
                    line.stroke "#1B9E77"
                ]

                Recharts.line [
                    line.dot false
                    line.dataKey (fun p -> p.SoilConductivity4)
                    line.name "Leitfähigkeit 4 [μS/cm]"
                    line.stroke "#D95F02"
                ]

                Recharts.cartesianGrid [ cartesianGrid.strokeDasharray [| 3; 3 |] ]
            ]
        ]
        |> fullHeightGraphBox "Verlauf der Bodenleitfähigkeit"

    [
        humidityChart
        temperatureChart
        conductivityChart
    ]